/**
 * Small behaviours for the Resilia (rs-*) sections:
 * - <rs-carousel>: scroll-snap track with looping prev/next buttons.
 * - <rs-accordion>: single-open accordion that animates the answer height.
 * - <rs-nav-drawer>: left slide-in navigation drawer opened from the header.
 * - <rs-disclosure>: country/currency picker in the footer.
 */

if (!customElements.get('rs-carousel')) {
  class RsCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-rs-track]');
      if (!this.track) return;
      this.querySelectorAll('[data-rs-prev]').forEach((button) => button.addEventListener('click', () => this.step(-1)));
      this.querySelectorAll('[data-rs-next]').forEach((button) => button.addEventListener('click', () => this.step(1)));
    }

    step(direction) {
      const track = this.track;
      const slide = track.firstElementChild;
      if (!slide) return;

      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const distance = slide.getBoundingClientRect().width + gap;
      const max = track.scrollWidth - track.clientWidth;
      let left = track.scrollLeft + direction * distance;

      if (direction > 0 && track.scrollLeft >= max - 2) left = 0;
      if (direction < 0 && track.scrollLeft <= 2) left = max;

      track.scrollTo({ left, behavior: 'smooth' });
    }
  }

  customElements.define('rs-carousel', RsCarousel);
}

if (!customElements.get('rs-accordion')) {
  class RsAccordion extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const heading = event.target.closest('[data-rs-accordion-heading]');
        if (!heading || !this.contains(heading)) return;
        event.preventDefault();
        this.toggle(heading.closest('[data-rs-accordion-item]'));
      });
    }

    toggle(item) {
      const open = this.querySelector('[data-rs-accordion-item].is-active');
      if (open && open !== item) this.setOpen(open, false);
      this.setOpen(item, !item.classList.contains('is-active'));
    }

    setOpen(item, isOpen) {
      const answer = item.querySelector('[data-rs-accordion-content]');
      const heading = item.querySelector('[data-rs-accordion-heading]');
      item.classList.toggle('is-active', isOpen);
      heading?.setAttribute('aria-expanded', String(isOpen));
      if (answer) answer.style.height = isOpen ? `${answer.scrollHeight + 30}px` : '0px';
    }
  }

  customElements.define('rs-accordion', RsAccordion);
}

if (!customElements.get('rs-nav-drawer')) {
  class RsNavDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.rs-drawer__panel');
      this.openers = document.querySelectorAll(`[data-rs-drawer-open="${this.id}"]`);

      this.openers.forEach((opener) => opener.addEventListener('click', () => this.open(opener)));
      this.querySelectorAll('[data-rs-drawer-close]').forEach((closer) => closer.addEventListener('click', () => this.close()));
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
    }

    open(opener) {
      this.lastOpener = opener;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      this.openers.forEach((button) => button.setAttribute('aria-expanded', 'true'));
      document.documentElement.setAttribute('scroll-lock', '');
      requestAnimationFrame(() => this.panel?.focus({ preventScroll: true }));
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      this.openers.forEach((button) => button.setAttribute('aria-expanded', 'false'));
      document.documentElement.removeAttribute('scroll-lock');
      this.lastOpener?.focus({ preventScroll: true });
    }
  }

  customElements.define('rs-nav-drawer', RsNavDrawer);
}

if (!customElements.get('rs-disclosure')) {
  class RsDisclosure extends HTMLElement {
    connectedCallback() {
      this.toggleButton = this.querySelector('[data-rs-disclosure-toggle]');
      this.list = this.querySelector('[data-rs-disclosure-list]');
      this.input = this.querySelector('[data-rs-disclosure-input]');
      if (!this.toggleButton || !this.list) return;

      this.toggleButton.addEventListener('click', () => this.setOpen(this.list.hidden));
      this.list.addEventListener('click', (event) => {
        const option = event.target.closest('[data-value]');
        if (!option) return;
        event.preventDefault();
        if (this.input) this.input.value = option.dataset.value;
        this.closest('form')?.submit();
      });
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.setOpen(false);
      });
      this.addEventListener('keyup', (event) => {
        if (event.key === 'Escape') {
          this.setOpen(false);
          this.toggleButton.focus();
        }
      });
    }

    setOpen(isOpen) {
      this.list.hidden = !isOpen;
      this.toggleButton.setAttribute('aria-expanded', String(isOpen));
    }
  }

  customElements.define('rs-disclosure', RsDisclosure);
}
